import React, { useState, useMemo } from "react";
import styled from "styled-components";
import {
  useGetPendingUsersQuery,
  useApproveUserMutation,
  useRejectUserMutation,
} from "../store/apis/adminApi";
import AdminLayout from "../components/AdminLayout";

export default function AdminDashboard() {
  const { data, isLoading } = useGetPendingUsersQuery();
  const [approveUser] = useApproveUserMutation();
  const [rejectUser] = useRejectUserMutation();

  const [search, setSearch] = useState("");

  const users = data?.users || [];

  const filteredUsers = useMemo(() => {
    return users.filter((user) =>
      `${user.name} ${user.email} ${user.organisation}`
        .toLowerCase()
        .includes(search.toLowerCase())
    );
  }, [users, search]);

  const handleApprove = async (id) => {
    try {
      await approveUser(id).unwrap();
    } catch (err) {
      console.error(err);
    }
  };

  const handleReject = async (id) => {
    try {
      await rejectUser(id).unwrap();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <AdminLayout>
      <Container>
        <Header>
          <Title>User Management</Title>
          <Subtitle>Approve or reject pending registration requests</Subtitle>
        </Header>

        <SearchInput
          placeholder="Search users by name, email or organisation..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <Card>
          {isLoading ? (
            <LoadingText>Loading pending users…</LoadingText>
          ) : (
            <Table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Organisation</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan="4" style={{ textAlign: "center", padding: "2rem" }}>
                      No pending users found.
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((user) => (
                    <tr key={user.id}>
                      <td>{user.name}</td>
                      <td>{user.email}</td>
                      <td>{user.organisation}</td>
                      <td>
                        <ApproveBtn onClick={() => handleApprove(user.id)}>
                          Approve
                        </ApproveBtn>
                        <RejectBtn onClick={() => handleReject(user.id)}>
                          Reject
                        </RejectBtn>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          )}
        </Card>
      </Container>
    </AdminLayout>
  );
}

// ----- Styled Components (matching the app’s style) -----
const Container = styled.div`
  color: white;
`;

const Header = styled.div`
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 2.2rem;
  font-weight: 600;
  margin: 0;
  background: linear-gradient(135deg, #ff6a00, #ee0979);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Subtitle = styled.p`
  color: #aaa;
  font-size: 1.1rem;
  margin-top: 0.2rem;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.9rem 1.2rem;
  margin-bottom: 1.5rem;
  border-radius: 10px;
  border: none;
  outline: none;
  font-size: 1rem;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(4px);
  color: white;
  transition: all 0.3s ease;

  &::placeholder {
    color: #aaa;
  }

  &:focus {
    background: rgba(255, 255, 255, 0.2);
    box-shadow: 0 0 0 2px #e55b41;
  }
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.08);
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.95rem;

  th,
  td {
    padding: 0.9rem 1rem;
    text-align: left;
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  }

  th {
    background: rgba(255, 255, 255, 0.04);
    font-weight: 600;
    color: #ddd;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-size: 0.8rem;
  }

  tr:hover td {
    background: rgba(255, 255, 255, 0.03);
  }
`;

const ActionButton = styled.button`
  padding: 0.4rem 1.2rem;
  border: none;
  border-radius: 20px;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.25s ease;
  margin-right: 0.6rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }

  &:last-child {
    margin-right: 0;
  }
`;

const ApproveBtn = styled(ActionButton)`
  background: #28a745;
  color: white;

  &:hover {
    background: #34d058;
  }
`;

const RejectBtn = styled(ActionButton)`
  background: #dc3545;
  color: white;

  &:hover {
    background: #ff4d4f;
  }
`;

const LoadingText = styled.p`
  text-align: center;
  padding: 2rem;
  color: #aaa;
  font-size: 1.1rem;
`;